'use client';

import { useId, useState } from 'react';
import type { MediaDto } from '@/lib/sanity/dto';
import { UiIcon } from '@/components/ui/UiIcon';
import { UiImage } from '@/components/ui/UiImage';
import { FULL_WIDTH_HINT, imageSizes } from '@/lib/utils/imageSizes';

type BeforeAfterProps = {
    title: string;
    hint: string;
    beforeImage: MediaDto;
    beforeLabel: string;
    afterImage: MediaDto;
    afterLabel: string;
    sliderLabel: string;
};

/**
 * Before/after comparison. The range input is the control — it is operable by
 * keyboard and announces its value, so the visual handle stays decorative.
 */
export function BeforeAfter({
    title,
    hint,
    beforeImage,
    beforeLabel,
    afterImage,
    afterLabel,
    sliderLabel,
}: BeforeAfterProps) {
    const [position, setPosition] = useState(50);
    const sliderId = useId();

    return (
        <section className="site-container section-pt-sm">
            <div className="surface-perla rounded-(--r-lg) bg-(--perla-surface)">
                <div className="mb-5.5 flex flex-wrap items-baseline justify-between gap-4">
                    <h2 className="m-0 font-(family-name:--font-display) text-xl font-medium tracking-[-0.01em] text-(--color-white)">
                        {title}
                    </h2>
                    <span className="text-sm text-(--text-color-secondary)">{hint}</span>
                </div>

                <div className="relative aspect-video overflow-hidden rounded-(--r) bg-(--perla-bg)">
                    <UiImage image={beforeImage} sizes={imageSizes(FULL_WIDTH_HINT)} altFallback={beforeLabel} />

                    <div className="absolute inset-0" style={{ clipPath: `inset(0 0 0 ${position}%)` }}>
                        <UiImage image={afterImage} sizes={imageSizes(FULL_WIDTH_HINT)} altFallback={afterLabel} />
                    </div>

                    <span className="shape-pill pointer-events-none absolute top-3.5 left-3.5 bg-(--perla-bg)/60 px-3 py-1 text-sm font-semibold tracking-[0.06em] text-(--perla-ink-dim) uppercase backdrop-blur-sm">
                        {beforeLabel}
                    </span>
                    <span className="shape-pill pointer-events-none absolute top-3.5 right-3.5 bg-(--accent-perla) px-3 py-1 text-sm font-semibold tracking-[0.06em] text-(--perla-accent-ink) uppercase">
                        {afterLabel}
                    </span>

                    <span
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-y-0 w-0.5 -translate-x-1/2 bg-white shadow-[0_0_12px_rgba(0,0,0,0.5)]"
                        style={{ left: `${position}%` }}
                    />
                    <span
                        aria-hidden="true"
                        className="pointer-events-none absolute top-1/2 flex size-10.5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-(--perla-accent-ink) shadow-[0_4px_14px_rgba(0,0,0,0.4)]"
                        style={{ left: `${position}%` }}
                    >
                        <UiIcon name="arrows-left-right" size={18} />
                    </span>

                    <label htmlFor={sliderId} className="sr-only">
                        {sliderLabel}
                    </label>
                    <input
                        id={sliderId}
                        type="range"
                        min={0}
                        max={100}
                        value={position}
                        onChange={(event) => setPosition(Number(event.target.value))}
                        className="absolute inset-0 m-0 size-full cursor-ew-resize opacity-0"
                    />
                </div>
            </div>
        </section>
    );
}
